/*
 * Copyright (c) 2019 by Enrico Ansaloni <enrico.ansaloni@gmail.com>
 */

import { EventEmitter } from 'events';
import AppController from "./AppController";
import _ from "lodash";
import moment from "moment";

//import {string} from "prop-types";
export class RestController extends EventEmitter {

    app: AppController|null = null;
    status: string = "ok";
    errors: any = null;
    message: string = "";
    abortController:AbortController|null = null;
    signal:AbortSignal|null = null;
    queue = new Map();

    constructor(app: AppController) {
        super();
        console.log(`RestController.tsx: constructor: start init...`);
        this.app = app;
        // this.abortController = new AbortController();
        // this.signal = this.abortController.signal;
        console.log(`RestController.tsx: constructor: init done`);
    }

    generateHeadersFromOptions: any = (query: string = "", select: string = "", skip: number = 0, limit: number = 0, sort: string = "", softDelete: number = 0) => {
        let headers: any = {
            'Content-type': 'application/json; charset=utf-8'
        };
        // prepare headers
        if (query !== "")
            headers["query"] = query;

        if (select !== "")
            headers["select"] = select;

        if (skip !== 0)
            headers["skip"] = skip;

        if (limit !== 0)
            headers["limit"] = limit;

        if (sort !== "")
            headers["sort"] = sort;

        if(softDelete !== 0)
            headers["softDelete"] = softDelete;

        headers["locale"] = this?.app?.getLocale();

        return headers;
    };

    generateHeadersFromObjectOptions = (query: any, select: any, skip: number = 0, limit: number = 0, sort:any, softDelete:number = 0, filter:any = {}) => {
        const headers: any = {
            'Content-type': 'application/json; charset=utf-8'
        };
        if(!_.isEmpty(query)) {
            headers["query"] = JSON.stringify(query);
        }
        if(!_.isEmpty(select)) {
            headers["select"] = JSON.stringify(select);
        }
        if (skip !== 0) {
            headers["skip"] = skip;
        }
        if(limit !== 0) {
            headers["limit"] = limit;
        }
        if(!_.isEmpty(sort)) {
            headers["sort"] = JSON.stringify(sort);
        }
        if(softDelete !== 0) {
            headers["softDelete"] = softDelete;
        }
        /*if(filter.length === 0) {
            const singleFilter = filter[0];
            headers["filter"] = JSON.stringify(singleFilter);
        } else if(filter.length > 0) {
            const filterArray = [];
            for(const ff of filter) {
                filterArray.push(JSON.stringify(ff));
            }
            headers["filter"] = JSON.stringify(filterArray);
        }*/
        if(!_.isEmpty(filter)) {
            headers["filter"] = JSON.stringify(filter);
        }
        return headers;

    }

    executeRequest = async (url: string, fetchOptions: any) => {
        // fetch response
        let method = fetchOptions["method"] || "get";
        if(!fetchOptions.headers) {
            fetchOptions.headers = {};
        }

        const abortController = new AbortController();
        const signal = abortController.signal;
        this.abortController = abortController;
        this.signal = signal;

        method = method.toUpperCase();
        try {

            // get auth info from AuthController
            let auth = this?.app?.auth.getAuth();
            // Set Authorization header if present
            if (auth && auth.accessToken) {
                // console.log(`RestController.tsx: executeRequest: setting authorization header...`);
                fetchOptions.headers["Authorization"] = `Bearer ${auth.accessToken}`;
            }

            const headerSize = this.calculateHeaderSize(fetchOptions.headers);
            if(headerSize > 8192) {
                throw new Error("Header size too large");
            }

            this.emit("requestPending");
            const response = await fetch(url, fetchOptions)
                .then(res => res.json());

            if (!signal?.aborted) {
                // console.log(`RestController.tsx: executeRequest: ${method}: HTTP(S) request successful [url=${url} - query=${fetchOptions.query}]`);
                //console.log(`DUMP RESULT`);
                //console.log(response);

                if (response?.status && response?.status === "ok") {
                    // request ok
                    // console.log(`RestController.tsx: executeRequest: ${method}: request status ok [url=${url} - query=${fetchOptions.query}]`);
                    this.emit("requestSuccessful");
                } else {
                    // request error
                    //if (response?.statusCode && (response?.statusCode === 401 || response?.statusCode === 403)) {
                    if (response?.statusCode && (response?.statusCode === 401)) {
                        // authorization needed
                        console.log(`RestController.tsx: executeRequest: ${method}: error: authorization needed [url=${url} - query=${fetchOptions?.query}]`);
                        this?.app?.auth.setAuthNeeded(true);
                    } else if(response?.statusCode && (response?.statusCode === 404)) {
                        response.status = "ok";
                        response.statusCode = 404;
                    } else {
                        // query/db error
                        console.log(`RestController.tsx: executeRequest: ${method}: error: query/db error [url=${url} - query=${fetchOptions?.query}]`);
                    }
                    this.emit("requestFailed");
                }

                // testing long query to test abort
                //await this.timeout(3000);

                return response;

            } else {
                // empty response
                console.log(`RestController.tsx: executeRequest: ${method}: error: empty response [url=${url} - query=${fetchOptions?.query}]`);
                this.emit("requestFailed");
                return {
                    status: "error",
                    statusCode: 404,
                    errors: "Empty HTTP response"
                };
            }

        } catch (error) {
            // http error
            if(!signal?.aborted) {
                console.log(`RestController.tsx: executeRequest: ${method}: HTTP(S) request error [url=${url} - query=${fetchOptions?.query}]`);
                this.emit("requestFailed");
                return {
                    status: "error",
                    statusCode: 500,
                    errors: error
                };
            } else {
                console.log(`RestController.tsx: executeRequest: ${method}: Request aborted [url=${url} - query=${fetchOptions?.query}]`);
                this.emit("requestAborted");
                return {
                    status: "ok",
                    statusCode: 200,
                    errors: "Request aborted"
                };
            }

        }
    };

    queueRequest = async (method:"GET", schema:string, id:string = "", query:any = {}, select:any = {}, skip:number = 0, limit:number = 0, sort:any = {}, filter:any = {}) => {
        const queueKey = `GET::${schema}::${id}::${JSON.stringify(query)}::${JSON.stringify(select)}::${skip}::${limit}::${JSON.stringify(sort)}::${JSON.stringify(filter)}`;
        if(this.queue.has(queueKey)) {
            return {
                status: "error",
                statusCode: 500,
                message: "Queue request failed",
                errors: "Queue request failed",
                data: []
            }
        } else {
            this.queue.set(queueKey, {
                schema: schema,
                id: id,
                query: query,
                select: select,
                skip: skip,
                limit: limit,
                sort: sort,
                filter: filter
            });
        }
    }

    calculateHeaderSize = (headers:any) => {
        const headerString = Object.entries(headers)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\r\n');
        return new Blob([headerString]).size;  // Blob.size returns the size in bytes
    };

    // schema, id, query, select, skip, limit, sort
    /*getData: any = async (schema: string, id: string = "", query: string = "", select: string = "", skip: number = 0, limit: number = 0, sort: string = "") => {
        // base URL
        let url = `/api/${schema}`;
        let fetchOptions: any = {
            headers: null
        };
        // add id to URL if present
        if (id !== "") {
            url += `/${id}`;
        }
        // generate header list
        // sync headers to fetch options
        fetchOptions.headers = this.generateHeadersFromOptions(query, select, skip, limit, sort);

        // console.log(`RestController.tsx: getData: about to get => url: ${url} - schema: ${schema} - id: ${id} - query: ${query} - select: ${select} - skip: ${skip} - limit: ${limit} - sort: ${sort}`);
        // fetch response
        return await this.executeRequest(url, fetchOptions);
    };*/

    getData: any = async (schema:string, id:string = "", query:any = {}, select:any = {}, skip:number = 0, limit:number = 0, sort:any = {}, filter:any = {}) => {
        // base URL
        let url = `/api/${schema.toLowerCase()}`;
        let fetchOptions: any = {
            headers: null
        };
        // add id to URL if present
        if (id !== "") {
            url += `/${id}`;
        }
        // generate header list
        // sync headers to fetch options
        // const queryString = JSON.stringify(query);
        // const selectString = JSON.stringify(select);
        // const sortString = JSON.stringify(sort);
        // fetchOptions.headers = this.generateHeadersFromOptions(queryString, selectString, skip, limit, sortString);
        fetchOptions.headers = this.generateHeadersFromObjectOptions(query, select, skip, limit, sort, 0, filter);

        // console.log(`RestController.tsx: getData: about to get => url: ${url} - schema: ${schema} - id: ${id} - query: ${query} - select: ${select} - skip: ${skip} - limit: ${limit} - sort: ${sort}`);
        // fetch response
        return await this.executeRequest(url, fetchOptions);
    }

    postData: any = async (schema: string, data: any) => {

        // base URL
        let url = `/api/${schema}`;
        let fetchOptions: any = {
            headers: null,
            method: null,
            body: null
        };
        // generate header list
        // sync headers to fetch options
        fetchOptions.headers = this.generateHeadersFromOptions();

        // add post fetch options
        fetchOptions.method = "post";
        fetchOptions.body = JSON.stringify(data);

        // console.log(`RestController.tsx: postData: about to post => url: ${url} - data:${JSON.stringify(data)}`);
        // fetch response
        return await this.executeRequest(url, fetchOptions);
    };

    // schema, id, query, sort, data
    putData: any = async (schema: string, id: string, query: any = "", sort: any = "", data: any) => {

        // base URL
        let url = `/api/${schema}`;
        // add id to URL
        url += `/${id}`;

        let fetchOptions: any = {
            headers: null,
            method: null,
            body: null
        };
        // generate header list
        // sync headers to fetch options
        fetchOptions.headers = this.generateHeadersFromObjectOptions(query, null, 0, 0, sort);

        // add put fetch options
        fetchOptions.method = "put";
        fetchOptions.body = JSON.stringify(data);

        // console.log(`RestController.tsx: putData: about to put => url: ${url} - id: ${id} - query: ${query} - sort: ${sort} - data:${data}`);
        // fetch response
        return await this.executeRequest(url, fetchOptions);
    };

    deleteData: any = async (schema: string, id: string = "", query: any, softDelete: number = 0) => {
        // base URL
        let url = `/api/${schema}`;
        let fetchOptions: any = {
            headers: null,
            method: null
        };
        // add id to URL if present
        if (id !== "") {
            url += `/${id}`;
        }
        // generate header list
        // sync headers to fetch options
        fetchOptions.headers = this.generateHeadersFromObjectOptions(query, null, 0, 0, null, softDelete);
        // add put fetch options
        fetchOptions.method = "delete";

        // console.log(`RestController.tsx: deleteData: about to delete => url: ${url} - id: ${id} - query: ${query} - softDelete: ${softDelete}`);
        // fetch response
        return await this.executeRequest(url, fetchOptions);
    };

    convertQueryObjectToString = (input:any) => {
        return JSON.stringify(input);
    }

    convertQueryStringToObject = (input:string) => {
        // TODO: add capability to parse $in expressions
        let result:any = {};
        if(input) {
            if(input.match(/^{/)) {
                // complex: whole json object
                try {
                    result = JSON.parse(input, (key, value) => {
                        if(key === "$regex") {
                            let matches = value.match(/^\/(.*)\/([imxs]*)$/);
                            if (matches && matches.length > 1) {
                                const searchTerm = matches[1];
                                const options = matches[2] || "";
                                return new RegExp(searchTerm, options);
                            } else {
                                return value;
                            }
                        } else if (key === "$and" || key === "$or") {
                            if(Array.isArray(value)) {
                                return value;
                            } else {
                                throw new Error(`argument to ${key} is not an array`);
                            }
                        } else {
                            return value;
                        }
                    });
                } catch (e) {
                    //console.log(`Utilities.js: parseOptions: cannot parse query string: ${e}`);
                    console.debug(`Cannot parse query string: ${e}`);
                    console.debug(`Query String: ${input}`);
                    throw new Error(`parseOptions: Cannot parse query string: ${e}`);
                    //result = {};
                }
            } else {
                // simple: comma separated values
                let items = input.split(/ *, */);
                if (items.length <= 0)
                    items = [input];
                for (const i in items) {
                    let item = items[i];
                    let parts = item.split(/: */);
                    if (parts.length >= 1) {
                        let key = parts.shift() || "";
                        result[key] = parts.join(':');
                    }
                }
            }
        }
        return result;
    };

    abortRequest = () => {
        this?.abortController?.abort();
        this.abortController = null;
        console.log("RestController.tsx: request aborted.")
    }

    generateSortObject = (schema:string, field:string, direction:number) => {
        let result:any = {};
        switch(schema.toLowerCase()) {
            case "user":
                if(field !== "status") {
                    result = {
                        status: 1,
                    }
                    result[field] = direction;
                } else {
                    result[field] = direction;
                }
                break;
            case "group":
                result[field] = direction;
                break;
            case "device":
                result[field] = direction;
                break;
            case "devicetype":
                result[field] = direction;
                break;
            case "report":
                /*
                if (field === "reports") {
                    sortString = "";
                } else if (field === "user") {
                    sortString = `user.name.last: ${direction}, user.name.first: ${direction}`;
                } else {
                    sortString = `${field}: ${direction}`;
                }
                */
                if(field === "report") {
                    result = {}
                } else if (field === "user") {
                    result = {"user.name.last": direction, "user.name.first": direction};
                } else {
                    result[field] = direction;
                }
                break;
            case "organization":
                result[field] = direction;
                break;
            case "belongsTo.name":
                result[field] = direction;
                break;
            default:
                result[field] = direction;
                break;

        }

        return result;
    }

    generateQueryObject = (schema:string, filter:any = {}, preFilter:any = {}, additionalData:any = {}) => {
        let query:any = {}
        let result:any = {};
        switch (schema.toLowerCase()) {
            case "user":
                if(filter?.field !== "disabled" && filter?.value !== "") {
                    switch(filter.field) {
                        case "group":
                            query = {"group.name":{$eq: `${filter?.value}`}}
                            break;
                        case "status":
                            query = {status: {$eq: `${filter?.value}`}}
                            break;
                        case "name":
                            const [first, last] = filter?.value.split(/ /);
                            if(first && last) {
                                const firstPattern = `.*${first}.*`;
                                const lastPattern = `.*${last}.*`;
                                query = { $and: [ {"name.first": {$regex: firstPattern, $options: "i"}}, {"name.last": {$regex: lastPattern, $options: "i"}} ]}
                            } else {
                                const namePattern = `.*${filter?.value}.*`
                                query = { $or: [ {"name.first": {$regex: namePattern, $options: "i"}}, {"name.last": {$regex: namePattern, $options: "i"}} ]}
                            }
                            break;
                        default:
                            const valuePattern = `.*${filter?.value}.*`;
                            query[filter?.field] = {$regex: valuePattern, $options: "i"};
                            break;
                    }
                }
                break;
            case "group":
                break;
            case "device":
                if(filter.field !== "disabled" && filter.value !== "") {
                    if(filter.field === "type") {
                        const deviceTypes = additionalData?.deviceTypes || [];
                        let foundType = deviceTypes.find((type: any) => (type.name === filter.value));
                        if (foundType) {
                            // query = `{"${filter.field}":{"$eq":"${foundType._id}"}}`;
                            query = {type: {$eq: `${foundType._id}`}}
                        } else {
                            // query = `{"type.name":{"$eq":"${filter.value}"}}`;
                            query = {"type.name": {$eq: `${filter?.value}`}}
                        }
                    } else if (filter.field === "macAddress") {
                        let searchValue;
                        if (filter.value.match(/:/)) {
                            searchValue = filter.value;
                        } else {
                            searchValue = filter.value.replace(/(.{2})/g, "$1:").replace(/:$/, "");
                        }
                        // query = `{"macAddress":{"$regex":"/.*${searchValue}$/i"}}`;
                        const valuePattern = `.*${searchValue}.*`
                        query = {macAddress: {$regex: valuePattern, $options: "i"}};
                        // const valueRegex = new RegExp(valuePattern, "i");
                        // query = {macAddress: {$eq: searchValue}};
                    } else if (filter.field === "model" || filter.field === "algorithm" || filter.field === "status") {
                        // query = `{"${filter.field}":{"$eq":"${filter.value}"}}`;
                        query[filter?.field] = {$eq: `${filter?.value}`}

                    } else {
                        // query = `{"${filter.field}":{"$regex": "/.*${filter.value}.*/i"}}`;
                        const valuePattern = `.*${filter?.value}.*`;
                        query[filter?.field] = {$regex: valuePattern, $options: "i"};
                    }
                }
                break;
            case "devicetype":
                if(filter.field !== "disabled" && filter.value !== "") {
                    if(filter.field === "maxUsers") {
                        //query = `{ "${filter.field}": { "$gte": ${parseInt(filter.value)} } }`;
                        query[filter?.field] = {$gte: parseInt(filter.value)};
                    } else {
                        const valuePattern = `.*${filter?.value}.*`;
                        query[filter?.field] = { $regex: valuePattern, $options: "i" };
                    }
                }
                break;
            case "report":
                if(filter.field !== "disabled" && filter.value !== "") {
                    if(filter.field === "user.mail") {
                        // query = `{ "user.mail": { "$regex": "/.*${filter.value}.*!/i" } }`;
                        const valuePattern = `.*${filter?.value}.*`
                        query = {"user.mail": {$regex: valuePattern, $options: "i"}}
                    } else if (filter.field === "user.name") {
                        // query = `{ "$or": [ { "user.name.first": { "$regex": "/.*${filter.value}.*!/i"}}, { "user.name.last": { "$regex": "/.*${filter.value}.*!/i"}} ]}`;
                        const [first, last] = filter?.value.split(/ /);
                        if(first && last) {
                            const firstPattern = `.*${first}.*`;
                            const lastPattern = `.*${last}.*`;
                            query = { $and: [ {"user.name.first": {$regex: firstPattern, $options: "i"}}, {"user.name.last": {$regex: lastPattern, $options: "i"}} ]}
                        } else {
                            const namePattern = `.*${filter?.value}.*`
                            query = { $or: [ {"user.name.first": {$regex: namePattern, $options: "i"}}, {"user.name.last": {$regex: namePattern, $options: "i"}} ]}
                        }
                        // const valuePattern = `.*${filter?.value}.*`
                        // query = { $or: [ {"user.name.first": {$regex: valuePattern, $options: "i"}}, {"user.name.last": {$regex: valuePattern, $options: "i"}} ]};
                    } else if (filter.field === "date") {
                        // query = `{ "date": { "$gte": "${moment(filter.value).toISOString()}" } }`
                        if (filter.value.match(/ => /)) {
                            const [start, end] = filter.value.split(/ => /);
                            if (start !== "" && end !== "") {
                                query = {date: {$gte: moment(start).toISOString(), $lte: moment(end).toISOString()}};
                            }
                        } else {
                            query = {date: {$gte: moment(filter.value).toISOString()}}
                        }
                        /*const dateString = moment(filter.value).toString();
                        const [start, end] = dateString.split(" - ");
                        const valueStartDate = moment(start).toISOString();
                        const valueEndDate = moment(end).toISOString();
                        if(end) {
                            query = {date: {$gte: valueStartDate, $lte: valueEndDate}};
                        } else {
                            query = {date: { $gte: valueStartDate}};
                        }*/
                    } else if (filter.field === "macAddress") {
                        let searchValue, searchValueWithoutDots;
                        if (filter.value.match(/:/)) {
                            searchValueWithoutDots = filter.value;
                            searchValue = this.formatMACAddress(filter.value);
                        } else {
                            searchValue = filter.value.replace(/(.{2})/g, "$1:").replace(/:$/, "");
                            searchValueWithoutDots = filter.value;
                        }
                        query = {$or: [{macAddress: searchValue}, {macAddress: searchValueWithoutDots}]};
                    } else if (filter.field === "organization") {
                        // query =`{ "user.belongsTo.name": { "$regex": "/.*${filter.value}.*!/i"}}`;
                        const valuePattern = `.*${filter?.value}.*`;
                        query = {"user.belongsTo.name": {$regex: valuePattern, $options: "i"}};
                    } else {
                        // query = `{ "${filter.field}": { "$regex": "/.*${filter.value}.*!/i" } }`;
                        const valuePattern = `.*${filter?.value}.*`
                        query[filter?.field] = {$regex: valuePattern, $options: "i"};
                    }
                }
                break;
            case "organization":
                if(filter.field === "type") {
                    query[filter?.field] = {$eq: `${filter?.value}`}
                } else if (filter.field === "name") {
                    const valuePattern = `.*${filter?.value}.*`;
                    query[filter?.field] = {$regex: valuePattern, $options: "i"};
                }
                break;
            default:
                break;
        }
        if(Object.keys(preFilter).length > 0) {
            if(Object.keys(preFilter).length > 0 && Object.keys(query).length > 0 ) {
                result = this.mergeMongoQueries(preFilter, query)
            } else if(Object.keys(preFilter).length <= 0 && Object.keys(query).length > 0) {
                result = query;
            } else if (Object.keys(preFilter).length > 0 && Object.keys(query).length <= 0) {
                result = preFilter;
            } else {
                result = query;
            }
        } else {
            result = query;
        }
        return result;
    }

    /*generateFinalQuery = (schema:string, preFilter:any, query:any) => {
        let result;
        if(Object.keys(preFilter).length > 0 && Object.keys(query).length > 0 ) {
            result = this.mergeMongoQueries(preFilter, query)
        } else if(Object.keys(preFilter).length <= 0 && Object.keys(query).length > 0) {
            result = query;
        } else if (Object.keys(preFilter).length > 0 && Object.keys(query).length <= 0) {
            result = preFilter;
        } else {
            result = query;
        }
        return result;
    }*/

    /*mergeMongoQueries = (query1: any, query2: any): any => {
        // Helper function to merge two arrays of queries, considering nested operators
        const mergeQueryArrays = (arr1: any[], arr2: any[]): any[] => {
            return [...arr1.map(item => mergeQueryObjects(item, item)), ...arr2.map(item => mergeQueryObjects(item, item))];
        };

        // Recursive function to merge objects considering MongoDB logic
        const mergeQueryObjects = (obj1: any, obj2: any): any => {
            const keys1 = Object.keys(obj1);
            const keys2 = Object.keys(obj2);
            const intersection = keys1.filter(key => keys2.includes(key));

            let result: any = {...obj1}; // Start with the first object

            // Apply second object's properties to the result
            keys2.forEach(key => {
                if (!result.hasOwnProperty(key)) {
                    result[key] = obj2[key];
                } else if (key === '$and' || key === '$or') {
                    // Recursively merge $and and $or arrays
                    result[key] = mergeQueryArrays(result[key], obj2[key]);
                } else if (typeof result[key] === 'object' && typeof obj2[key] === 'object') {
                    // Recursively merge objects
                    result[key] = mergeQueryObjects(result[key], obj2[key]);
                } else {
                    // For overlapping keys not under logical operators, wrap them in $and
                    result.$and = result.$and || [];
                    result.$and.push({[key]: obj1[key]}, {[key]: obj2[key]});
                    delete result[key];
                }
            });

            return result;
        };
        return mergeQueryObjects(query1, query2);
    }*/

    /*mergeMongoQueries = (query1:any, query2:any) => {
        // Helper function to merge two arrays of queries, considering nested operators
        const mergeQueryArrays = (arr1:any, arr2:any) => {
            return [...arr1, ...arr2];
        };

        // Recursive function to merge objects considering MongoDB logic
        const mergeQueryObjects = (obj1:any, obj2:any) => {
            const keys1 = Object.keys(obj1);
            const keys2 = Object.keys(obj2);

            let result = {...obj1}; // Start with the first object

            // Apply second object's properties to the result
            keys2.forEach(key => {
                if (!result.hasOwnProperty(key)) {
                    result[key] = obj2[key];
                } else if (key === '$and') {
                    // Recursively merge $and arrays
                    result[key] = mergeQueryArrays(result[key], obj2[key]);
                } else if (key === '$or') {
                    // Handle $or when both queries contain $or differently
                    if (result.$or) {
                        // If both have $or, wrap merged $or arrays in an $and to keep logical separation
                        result.$and = result.$and || [];
                        result.$and.push({ $or: mergeQueryArrays(result.$or, obj2.$or) });
                        delete result.$or;
                    } else {
                        // If only one has $or, simply concatenate the arrays
                        result[key] = mergeQueryArrays(result[key], obj2[key]);
                    }
                } else if (typeof result[key] === 'object' && typeof obj2[key] === 'object' && !Array.isArray(result[key])) {
                    // Recursively merge objects
                    result[key] = mergeQueryObjects(result[key], obj2[key]);
                } else {
                    // For overlapping keys not under logical operators, wrap them in $and
                    result.$and = result.$and || [];
                    result.$and.push({[key]: obj1[key]}, {[key]: obj2[key]});
                    delete result[key];
                }
            });

            return result;
        };
        return mergeQueryObjects(query1, query2);
    };*/

    mergeMongoQueries = (query1:any, query2:any) => {
        // Function to check if an object has any keys except the ones listed
        const hasOtherKeys = (obj:any, ignoreKeys:any) => {
            return Object.keys(obj).some(key => !ignoreKeys.includes(key));
        };

        // Initialize the merged result with $and to combine the queries
        let mergedResult:any = { $and: [] };

        // Check if either query has $or or other keys and handle accordingly
        if (query1.$or && hasOtherKeys(query1, ['$or']) || query2.$or && hasOtherKeys(query2, ['$or'])) {
            // Include other keys from both queries excluding $or
            let filteredQuery1 = {...query1};
            let filteredQuery2 = {...query2};
            delete filteredQuery1.$or;
            delete filteredQuery2.$or;

            // Add the filtered queries to the $and array if they're not empty
            if (Object.keys(filteredQuery1).length) mergedResult.$and.push(filteredQuery1);
            if (Object.keys(filteredQuery2).length) mergedResult.$and.push(filteredQuery2);

            // Add $or conditions if present
            if (query1.$or) mergedResult.$and.push({ $or: query1.$or });
            if (query2.$or) mergedResult.$and.push({ $or: query2.$or });
        } else {
            // If there are no $or conditions, combine all keys using $and
            mergedResult.$and.push(query1);
            mergedResult.$and.push(query2);
        }

        // Simplify the $and array if it only contains one element
        if (mergedResult.$and.length === 1) {
            mergedResult = mergedResult.$and[0];
        }

        return mergedResult;
    };

    calculateSkipAndLimit = (currentPage:number, itemsPerPage:number) => {
        const skip = currentPage * itemsPerPage;
        const limit = itemsPerPage;
        return {
            skip: skip,
            limit: limit
        }
    }

    formatMACAddress = (mac:string) => {
        const formatted = [];
        for (let i = 0; i < mac.length; i += 2) {
            formatted.push(mac.slice(i, i + 2));
        }
        // Add the dots between each pair of characters
        return formatted.join(':');
    }

    // for simulating slow response
    timeout = (ms:number) => {
        return new Promise(resolve => setTimeout(resolve, ms));
    }


}

export default RestController;